import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { expirationDurations } from 'services/cookie.service'

export type RightPanelType = 'colors' | 'probability' | 'manaCurve' | 'qunatities' | 'condensed'
export type InteractionType = 'description' | 'comments'

type RightPanelStatus = {
  open: boolean
  locked: boolean
  type: RightPanelType
}

type InteractionStatus = {
  open: boolean
  locked: boolean
  type: InteractionType
}

type StaticCardStatus = {
  open: boolean
}

export const useRightPanel = (): [RightPanelStatus, (updatedStatus: RightPanelStatus) => void] => {
  const [{ rpOpen, rpLock, rpType: type = 'condensed' }, setCookies] = useCookies(['rpOpen', 'rpLock', 'rpType'])

  const open = rpOpen === 'true'
  const locked = rpLock === 'true'

  const [rightPanelStatus, setRightPanelStatus] = useState<RightPanelStatus>({ open, locked, type })

  const handleSetNewStatus = (rightPanelStatus: RightPanelStatus) => {
    setCookies('rpOpen', `${rightPanelStatus.open}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('rpLock', `${rightPanelStatus.locked}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('rpType', rightPanelStatus.type, { path: '/', maxAge: expirationDurations.year })
  }

  useEffect(() => {
    // If they locked the panel and the screen to smaller than we'd allow the locked panel to be, unlock it
    // This is a bit un-elegant since it'll trigger a re-render immediately after the inital render, but better than having a locked panel on a phone
    if (locked && window.innerWidth < 600) handleSetNewStatus({ open: false, locked: false, type })
  }, [])

  useEffect(() => {
    setRightPanelStatus({ open, locked, type })
  }, [rpOpen, rpLock, type])

  return [rightPanelStatus, handleSetNewStatus]
}

export const usePinnedInteractions = (): [InteractionStatus, (updatedStatus: InteractionStatus) => void] => {
  const [{ interactionOpen, interactionLocked, interactionType: type = 'description' }, setCookies] = useCookies([
    'interactionOpen',
    'interactionLocked',
    'interactionType',
  ])

  const open = interactionOpen === 'true'
  const locked = interactionLocked === 'true'

  const [interactionPanelStatus, setInteractionPanelStatus] = useState<InteractionStatus>({ open, locked, type })

  const handleSetNewStatus = (rightPanelStatus: InteractionStatus) => {
    setCookies('interactionOpen', `${rightPanelStatus.open}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('interactionLocked', `${rightPanelStatus.locked}`, { path: '/', maxAge: expirationDurations.year })
    setCookies('interactionType', rightPanelStatus.type, { path: '/', maxAge: expirationDurations.year })
  }

  useEffect(() => {
    // If they locked the panel and the screen to smaller than we'd allow the locked panel to be, unlock it
    // This is a bit un-elegant since it'll trigger a re-render immediately after the inital render, but better than having a locked panel on a phone
    if (locked && window.innerWidth < 800) handleSetNewStatus({ open: false, locked: false, type })
  }, [])

  useEffect(() => {
    setInteractionPanelStatus({ open, locked, type })
  }, [interactionOpen, interactionLocked, type])

  return [interactionPanelStatus, handleSetNewStatus]
}

export const usePinnedStaticCard = (): [StaticCardStatus, (updatedStatus: StaticCardStatus) => void] => {
  const [{ staticCardOpen }, setCookies] = useCookies(['staticCardOpen'])

  const open = staticCardOpen === 'true'

  const [staticCardStatus, setStaticCardStatus] = useState<StaticCardStatus>({ open })

  const handleSetNewStatus = (staticCardStatus: StaticCardStatus) => {
    setCookies('staticCardOpen', `${staticCardStatus.open}`, { path: '/', maxAge: expirationDurations.year })
  }

  useEffect(() => {
    // If they locked the panel and the screen to smaller than we'd allow the locked panel to be, unlock it
    // This is a bit un-elegant since it'll trigger a re-render immediately after the inital render, but better than having a locked panel on a phone
    if (open && window.innerWidth < 800) handleSetNewStatus({ open: false })
  }, [])

  useEffect(() => {
    setStaticCardStatus({ open })
  }, [staticCardOpen])

  return [staticCardStatus, handleSetNewStatus]
}
